/*
  Feature story behaviour (site-behaviour-spec.md > Scroll reveals and counters, Drag to compare,
  Video-synced tabs and lists, Progress bars). Every part honours prefers-reduced-motion.
*/
// Section scripts can be included more than once on a page; everything runs once.
if (!window.BBStory) {
window.BBStory = true;
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Reveals: items fade up as they enter view, 80 ms apart within a row */
  const reveal = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-in');
      reveal.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -10% 0px' });

  /* Counters: numbers in a stat count up from 0 once, the first time they show */
  const countUp = (el) => {
    const text = el.textContent;
    const parts = text.split(/(\d[\d,.]*)/);
    if (parts.length < 2 || reduced) return;
    const targets = parts.map((p, i) => (i % 2 ? parseFloat(p.replace(/,/g, '')) : null));
    const decimals = parts.map((p, i) => (i % 2 && p.includes('.') ? p.split('.')[1].length : 0));
    const start = performance.now();
    const duration = 1200;
    const frame = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = parts.map((p, i) => {
        if (!(i % 2)) return p;
        const v = targets[i] * eased;
        return v.toLocaleString('en-AU', { minimumFractionDigits: decimals[i], maximumFractionDigits: decimals[i] });
      }).join('');
      if (t < 1) requestAnimationFrame(frame); else el.textContent = text;
    };
    requestAnimationFrame(frame);
  };
  const counters = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      countUp(entry.target);
      counters.unobserve(entry.target);
    });
  }, { threshold: 0.6 });

  const init = (root) => {
    root.querySelectorAll('.story-row').forEach((row) => {
      [...row.querySelectorAll(':scope > .reveal')].forEach((el, i) => el.style.setProperty('--reveal-delay', `${i * 80}ms`));
    });
    root.querySelectorAll('.reveal').forEach((el) => reveal.observe(el));
    root.querySelectorAll('[data-count-up]').forEach((el) => counters.observe(el));
    root.querySelectorAll('.story-block--media').forEach((block) => {
      const video = block.querySelector('video');
      video?.addEventListener('playing', () => block.classList.add('is-playing'));
    });
  };
  const start = () => init(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();

/* Drag to compare: drag or swipe anywhere on the image. Sweeps once as a hint, then rests in the middle */
class DragCompare extends HTMLElement {
  connectedCallback() {
    this.frame = this.querySelector('.compare__frame');
    this.range = this.querySelector('.compare__range');
    const set = (pct) => {
      const v = Math.max(0, Math.min(100, pct));
      this.style.setProperty('--pos', `${v}%`);
      this.range.value = v;
    };
    const fromEvent = (e) => {
      const r = this.frame.getBoundingClientRect();
      set(((e.clientX - r.left) / r.width) * 100);
    };
    this.frame.addEventListener('pointerdown', (e) => {
      this.dragging = true;
      this.frame.setPointerCapture(e.pointerId);
      fromEvent(e);
    });
    this.frame.addEventListener('pointermove', (e) => { if (this.dragging) fromEvent(e); });
    this.frame.addEventListener('pointerup', () => { this.dragging = false; });
    this.frame.addEventListener('pointercancel', () => { this.dragging = false; });
    this.range.addEventListener('input', () => set(Number(this.range.value)));

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      const keys = [50, 25, 75, 50];
      const t0 = performance.now();
      const step = (now) => {
        if (this.dragging) return;
        const t = Math.min(1, (now - t0) / 1600);
        const seg = Math.min(keys.length - 2, Math.floor(t * (keys.length - 1)));
        const local = t * (keys.length - 1) - seg;
        const ease = 0.5 - Math.cos(local * Math.PI) / 2;
        set(keys[seg] + (keys[seg + 1] - keys[seg]) * ease);
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, { threshold: 0.6 });
    io.observe(this);
  }
}
if (!customElements.get('drag-compare')) customElements.define('drag-compare', DragCompare);

/* Video-synced tabs: the active tab follows the clip's cue points; its orange bar fills over the
   segment. Tapping a tab jumps the clip there. Same on desktop tabs and the mobile list */
class VideoTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('.story-tab')];
    const holder = this.dataset.video && document.getElementById(this.dataset.video);
    this.video = holder?.querySelector('video');
    this.starts = this.tabs.map((t) => Number(t.dataset.start) || 0);
    this.tabs.forEach((tab, i) => tab.addEventListener('click', () => {
      if (this.video && this.video.readyState > 0) {
        this.video.currentTime = this.starts[i] + 0.05;
        this.video.play().catch(() => {});
      }
      this.show(i, 0);
    }));
    this.show(0, 0);
    if (!this.video) return;
    const tick = () => {
      const t = this.video.currentTime;
      const end = this.video.duration || this.starts[this.starts.length - 1] + 4;
      let i = this.starts.length - 1;
      while (i > 0 && t < this.starts[i]) i -= 1;
      const segEnd = this.starts[i + 1] ?? end;
      const progress = Math.max(0, Math.min(1, (t - this.starts[i]) / (segEnd - this.starts[i])));
      this.show(i, progress);
      if (!this.video.paused) this.raf = requestAnimationFrame(tick);
    };
    this.video.addEventListener('play', () => { cancelAnimationFrame(this.raf); this.raf = requestAnimationFrame(tick); });
    this.video.addEventListener('seeked', tick);
  }

  show(index, progress) {
    this.tabs.forEach((tab, i) => {
      const on = i === index;
      if (tab.getAttribute('aria-pressed') !== String(on)) tab.setAttribute('aria-pressed', String(on));
      const bar = tab.querySelector('.story-tab__bar i');
      if (bar) bar.style.width = on ? `${progress * 100}%` : '0%';
    });
  }
}
if (!customElements.get('video-tabs')) customElements.define('video-tabs', VideoTabs);

/* Image stepper: frames cross-fade every 4 s while on screen; one orange bar fills at a time.
   Bars jump to their frame. Reduced motion: no auto-advance */
class ImageStepper extends HTMLElement {
  connectedCallback() {
    this.frames = [...this.querySelectorAll('[data-step]')];
    this.barsEl = this.querySelector('.step-bars');
    if (this.frames.length < 2 || !this.barsEl) return;
    this.duration = 4000;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.bars = this.frames.map((frame, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-label', frame.querySelector('.step__title')?.textContent || `${i + 1}`);
      b.innerHTML = '<span><i></i></span>';
      b.addEventListener('click', () => this.go(i, true));
      this.barsEl.appendChild(b);
      return b;
    });
    this.barsEl.removeAttribute('aria-hidden');
    this.classList.add('is-ready');
    this.go(0);
    new IntersectionObserver((entries) => {
      this.visible = entries[0].isIntersecting;
      if (this.visible) this.loop(); else cancelAnimationFrame(this.raf);
    }, { threshold: 0.4 }).observe(this);
  }

  go(i, manual) {
    this.index = (i + this.frames.length) % this.frames.length;
    this.started = performance.now();
    this.frames.forEach((f, n) => f.classList.toggle('is-active', n === this.index));
    this.bars.forEach((b, n) => {
      b.setAttribute('aria-current', String(n === this.index));
      b.querySelector('i').style.width = this.reduced && n === this.index ? '100%' : '0%';
    });
    if (manual && this.visible) this.loop();
  }

  loop() {
    cancelAnimationFrame(this.raf);
    if (this.reduced) return;
    const frame = (now) => {
      const t = (now - this.started) / this.duration;
      if (t >= 1) { this.go(this.index + 1); }
      else this.bars[this.index].querySelector('i').style.width = `${t * 100}%`;
      this.raf = requestAnimationFrame(frame);
    };
    this.started = performance.now();
    this.raf = requestAnimationFrame(frame);
  }
}
if (!customElements.get('image-stepper')) customElements.define('image-stepper', ImageStepper);
}
